/**
 * What the reviewer has decided about a field's AI-extracted value.
 *
 * `rejected` means "the AI value is wrong and I have no replacement": the field is cleared and
 * the AI value stays available behind "Restore AI value". Typing a value afterwards makes it
 * `corrected`, as editing any AI field does.
 */
export const REVIEW_DECISIONS = {
  accepted: "accepted",
  corrected: "corrected",
  rejected: "rejected",
} as const;

export type ReviewDecision = (typeof REVIEW_DECISIONS)[keyof typeof REVIEW_DECISIONS];

/** Edited and rejected are both reviewer changes: the row offers restoration in place of Accept/Reject. */
export const isReviewerChange = (decision: ReviewDecision | undefined): boolean =>
  decision === REVIEW_DECISIONS.corrected || decision === REVIEW_DECISIONS.rejected;

/**
 * Sent on the wire in place of a decision when a previously saved one is being undone — never a
 * value `draft.decisions` itself holds, so it is not one of `REVIEW_DECISIONS`.
 */
export const REVERTED_DECISION = "reverted";

/**
 * The server's `review_status` values (lowercased), a wider vocabulary than `REVIEW_DECISIONS`:
 * "edited" is read back as a `corrected` decision; "rejected" as `rejected`.
 */
export const REVIEW_STATUSES = {
  accepted: "accepted",
  corrected: "corrected",
  edited: "edited",
  rejected: "rejected",
} as const;

/**
 * What has become of a value Theragenie proposed. `pending` blocks Save; `accepted` and
 * `rejected` go up in the PATCH as `agent_proposals`; `superseded` is a pending proposal a newer
 * one on the same field replaced, and `undone` one the reviewer stepped back from; neither is sent.
 */
export const PROPOSAL_DECISIONS = {
  pending: "pending",
  accepted: "accepted",
  rejected: "rejected",
  superseded: "superseded",
  undone: "undone",
} as const;
